import { useLingui } from '@lingui/react/macro';
import { styled } from '@linaria/react';
import { APP_PATHS } from 'app-shared/constants';
import { Dropdown } from 'app-ui/components';
import { IconChevronDown, IconPlus, IconSettings } from 'app-ui/icon';
import { Avatar, Pill } from 'app-ui/primitives/data-display';
import { MOBILE_VIEWPORT, themeCssVariables, useTheme } from 'app-ui/theme';
import { Link } from 'react-router-dom';

import { AUTH_MODE } from '@/auth/constants/AuthMode';
import { ClerkCreateWorkspaceItem } from '@/navigation/components/ClerkCreateWorkspaceItem';
import { ClerkWorkspaceOptionItems } from '@/navigation/components/ClerkWorkspaceOptionItems';
import { useCurrentWorkspace } from '@/session/hooks/useCurrentWorkspace';
import { useIsNavigationDrawerCollapsed } from '@/ui/layout/navigation-drawer/hooks/useIsNavigationDrawerCollapsed';
import { isMobileNavigationDrawerOpenState } from '@/ui/layout/navigation-drawer/states/isMobileNavigationDrawerOpenState';
import { HotkeyLayerEffect } from '@/ui/utilities/hotkey/effect-components/HotkeyLayerEffect';
import { useSetAtomState } from '@/ui/utilities/state/jotai/hooks/useSetAtomState';

const StyledTrigger = styled.button`
  align-items: center;
  background: transparent;
  border: none;
  border-radius: ${themeCssVariables.border.radius.md};
  box-sizing: border-box;
  color: ${themeCssVariables.font.color.primary};
  cursor: pointer;
  display: flex;
  font-family: inherit;
  font-size: ${themeCssVariables.font.size.md};
  font-weight: ${themeCssVariables.font.weight.semiBold};
  gap: ${themeCssVariables.spacing[2]};
  height: ${themeCssVariables.spacing[8]};
  min-width: 0;
  outline: none;
  padding: 0 ${themeCssVariables.spacing[1]};
  transition: ${themeCssVariables.clickableElementBackgroundTransition};
  width: 100%;
  @media (max-width: ${MOBILE_VIEWPORT}px) {
    height: ${themeCssVariables.spacing[11]};
  }

  &[data-popup-open],
  &:focus-visible {
    background: ${themeCssVariables.background.transparent.light};
  }

  &[data-collapsed] {
    justify-content: center;
    padding: 0;
    width: ${themeCssVariables.spacing[8]};
  }

  @media (hover: hover) {
    &:hover {
      background: ${themeCssVariables.background.transparent.light};
    }
  }
`;

const StyledMenuContent = styled(Dropdown.Content)`
  @media (max-width: ${MOBILE_VIEWPORT}px) {
    [role='menuitem'],
    [role='menuitemradio'] {
      min-height: ${themeCssVariables.spacing[11]};
    }
  }
`;
const StyledName = styled.span`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-align: left;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const StyledChevron = styled.span`
  align-items: center;
  color: ${themeCssVariables.font.color.tertiary};
  display: flex;
  flex: none;
`;

export const WorkspaceSwitcher = () => {
  const { t } = useLingui();
  const theme = useTheme();
  const currentWorkspace = useCurrentWorkspace();
  const isCollapsed = useIsNavigationDrawerCollapsed();
  const setIsMobileNavigationDrawerOpen = useSetAtomState(
    isMobileNavigationDrawerOpenState,
  );

  const handleLinkClick = () => {
    setIsMobileNavigationDrawerOpen(false);
  };

  return (
    <Dropdown.Root type="menu">
      <Dropdown.Trigger
        aria-label={t`Switch workspace`}
        render={<StyledTrigger data-collapsed={isCollapsed || undefined} />}
      >
        <Avatar name={currentWorkspace.name} size="lg" shape="rounded-square" />
        {!isCollapsed && (
          <>
            <StyledName>{currentWorkspace.name}</StyledName>
            <StyledChevron>
              <IconChevronDown
                size={theme.icon.size.sm}
                stroke={theme.icon.stroke.sm}
                aria-hidden
              />
            </StyledChevron>
          </>
        )}
      </Dropdown.Trigger>
      <StyledMenuContent aria-label={t`Workspaces`} width={240} sideOffset={4}>
        <HotkeyLayerEffect layer="overlay" />
        <Dropdown.Section>
          {AUTH_MODE === 'clerk' ? (
            <ClerkWorkspaceOptionItems />
          ) : (
            <Dropdown.OptionItem
              selected
              startIcon={
                <Avatar
                  name={currentWorkspace.name}
                  size="md"
                  shape="rounded-square"
                />
              }
            >
              {currentWorkspace.name}
            </Dropdown.OptionItem>
          )}
        </Dropdown.Section>
        <Dropdown.Separator />
        <Dropdown.Section>
          <Dropdown.ActionItem
            startIcon={<IconSettings />}
            render={<Link to={APP_PATHS.settingsWorkspace} />}
            onClick={handleLinkClick}
          >
            {t`Workspace settings`}
          </Dropdown.ActionItem>
          {AUTH_MODE === 'clerk' ? (
            <ClerkCreateWorkspaceItem />
          ) : (
            <Dropdown.ActionItem
              disabled
              startIcon={<IconPlus />}
              endIcon={<Pill label={t`Soon`} />}
            >
              {t`Create workspace`}
            </Dropdown.ActionItem>
          )}
        </Dropdown.Section>
      </StyledMenuContent>
    </Dropdown.Root>
  );
};
